/**
 * Utility functions for Homepage component
 *
 * Evaluate tutorial steps visibility based on cluster and dbSourceType
 * clusterTwo: C2
 * clusterThree: C3
 * nexiDirectISP: ACQUIRING_ISP
 * onlyPOS: ECGP
 */
import formattedMessage from './languageModule'
import componentConfig from './componentConfig'

export const TUTORIALS = {
  HOMEPAGE_TUTORIAL: 'HOMEPAGE_TUTORIAL'
}

// Compute acquiring flags starting from cluster and selectorGroups
export const computeAcquiringFlags = ({ cluster, dbSourceType }) => {
  const clusterTwo = cluster === 'C2'
  const clusterThree = cluster === 'C3'
  const isECGP = dbSourceType.includes('ECGP') && dbSourceType.length === 1 // only POS
  const isAcquiringISP = dbSourceType.includes('ACQUIRING_ISP') && dbSourceType.length === 1 // Nexi Direct ISP

  return {
    clusterTwo: !isECGP && !isAcquiringISP ? clusterTwo : false,
    clusterThree: !isECGP && !isAcquiringISP ? clusterThree : false,
    nexiDirectISP: isAcquiringISP,
    onlyPOS: isECGP
  }
}

// Build absolute image path depending on bank theme using centralized mapping
const buildImagePath = (fileName) => `${process.env.CMS_URL}/images/${fileName}`

// Static tutorial steps definition; visibility controlled by eligibility function below
const tutorialDefinition = {
  NEXIBUSINESS: {
    [TUTORIALS.HOMEPAGE_TUTORIAL]: [
      {
        key: 'homepage',
        title: () => formattedMessage.tutorialTitleHomepage,
        image: (f) => {
          if (f.clusterThree) {
            return 'tutorial_hpC3.webp'
          }
          return 'tutorial_hp.webp'
        },
        isElegibleFor: (f) => f.clusterTwo || f.clusterThree || f.nexiDirectISP || f.onlyPOS
      },
      {
        key: 'transactions',
        title: () => formattedMessage.tutorialTitleTransactions,
        image: () => 'tutorial_transactions.webp',
        isElegibleFor: (f) => f.clusterTwo || f.clusterThree || f.nexiDirectISP || f.onlyPOS
      },
      {
        key: 'results',
        title: () => formattedMessage.tutorialTitleResults,
        image: () => 'tutorial_results.webp',
        isElegibleFor: (f) => f.clusterTwo || f.nexiDirectISP || f.onlyPOS
      },
      {
        key: 'documents',
        title: () => formattedMessage.tutorialTitleDocuments,
        image: () => 'tutorial_documents.webp',
        isElegibleFor: (f) => f.clusterTwo || f.clusterThree || f.nexiDirectISP || f.onlyPOS
      },
      {
        key: 'menu',
        title: (f) => !f.nexiDirectISP ? formattedMessage.tutorialTitleMenu : formattedMessage.tutorialTitleMenuNexiDirectISP,
        image: (f) => {
          if (f.nexiDirectISP) {
            return 'tutorial_commission.webp'
          } else if (f.onlyPOS) {
            return 'tutorial_services.webp'
          } else {
            return 'tutorial_menu.webp'
          }
        },
        isElegibleFor: (f) => f.clusterTwo || f.clusterThree || f.nexiDirectISP || f.onlyPOS
      },
      {
        key: 'profile',
        title: () => formattedMessage.tutorialTitleProfile,
        image: () => 'tutorial_profile.webp',
        isElegibleFor: (f) => f.clusterTwo || f.clusterThree || f.nexiDirectISP || f.onlyPOS
      }
    ]
  },
  UCI: {
    [TUTORIALS.HOMEPAGE_TUTORIAL]: [
      {
        key: 'homepage',
        title: () => formattedMessage.tutorialTitleHomepage,
        image: () => 'tutorial_hp.webp',
        isElegibleFor: () => true
      },
      {
        key: 'transactions',
        title: () => formattedMessage.tutorialTitleTransactions,
        image: () => 'tutorial_transactions.webp',
        isElegibleFor: () => true
      },
      {
        key: 'results',
        title: () => formattedMessage.tutorialTitleResults,
        image: () => 'tutorial_results.webp',
        isElegibleFor: () => true
      },
      {
        key: 'documents',
        title: () => formattedMessage.tutorialTitleDocuments,
        image: () => 'tutorial_documents.webp',
        isElegibleFor: () => true
      },
      {
        key: 'menu',
        title: (f) => !f.nexiDirectISP ? formattedMessage.tutorialTitleMenu : formattedMessage.tutorialTitleMenuNexiDirectISP,
        image: () => 'tutorial_menu.webp',
        isElegibleFor: () => true
      },
      {
        key: 'profile',
        title: () => formattedMessage.tutorialTitleProfile,
        image: () => 'tutorial_profile.webp',
        isElegibleFor: () => true
      }
    ]
  }
}

// Return the list of eligible steps for a tutorial key
export const getEligibleTutorialSteps = ({
  tutorialKey,
  cluster,
  dbSourceType
}) => {
  const flags = computeAcquiringFlags({ cluster, dbSourceType })
  // Select tutorial variant by bank via centralized map; if not found, return []
  const variant = componentConfig.homepageTutorial.variant()
  if (!variant) return { flags, steps: [] }
  const tutorialDefinitionByBank = tutorialDefinition[variant]
  const def = tutorialDefinitionByBank[tutorialKey] || []
  const steps = def
    .filter(step => step.isElegibleFor(flags))
    .map(step => ({
      key: step.key,
      title: step.title(flags),
      img: buildImagePath(step.image(flags))
    }))

  return { flags, steps }
}

export default {
  TUTORIALS,
  computeAcquiringFlags,
  getEligibleTutorialSteps
}
